import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Box,
  Chip,
  ListItemAvatar,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { enqueueSnackbar } from "notistack";
import { useApiClient } from "../../../api";
import { FruitifyContainer } from "../../../shared-components/Containers";
import { read, act } from "./api";
import SegmentEnrollment from "./SegmentEnrollment";

export default function SegmentsPage() {
  const api = useApiClient().marketing;
  const { segmentId } = useParams();
  const navigate = useNavigate();
  const [segment, setSegment] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [kind, setKind] = useState("user");
  const [enrolling, setEnrolling] = useState(false);
  const [operations, setOperations] = useState([]);
  const [operationPage, setOperationPage] = useState(1);
  const [operationTotal, setOperationTotal] = useState(0);
  const [operation, setOperation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  const load = useCallback(async () => {
    try {
      if (segmentId) {
        const item = await read(api, "segment", { id: Number(segmentId) });
        setSegment(item);
        setName(item.name);
        setDescription(item.description);
        const operations = await read(api, "operations", {
          id: item.id,
          page: operationPage,
        });
        setOperations(operations.items);
        setOperationTotal(operations.total);
      } else setSegment(null);
      setError("");
    } catch (err) {
      setError(err.message);
    }
  }, [api, segmentId, operationPage]);
  useEffect(() => {
    load();
  }, [load]);
  const refreshMembers = async () => {
    await load();
    setRevision((value) => value + 1);
  };
  const mutate = async (action, data) => {
    setBusy(true);
    try {
      const response = await act(api, action, data);
      enqueueSnackbar("Saved.", { variant: "success" });
      await refreshMembers();
      return response;
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
      return null;
    } finally {
      setBusy(false);
    }
  };
  return (
    <FruitifyContainer>
      <Stack spacing={3}>
        <Typography variant="h4">{segment?.name || "Segments"}</Typography>
        {error && <Alert severity="error">{error}</Alert>}
        {segment ? (
          <Stack spacing={2}>
            <Button component={Link} to="/superuser/marketing/segments">
              All segments
            </Button>
            <Typography>
              {segment.memberCount} members ·{" "}
              {segment.automatic
                ? "Automatically managed by current school approval and account type"
                : segment.kind === "contact"
                  ? "Outreach contacts"
                  : "Users"}
            </Typography>
            <Button variant="contained" onClick={() => setEnrolling(true)}>
              Enroll to workflow
            </Button>
          </Stack>
        ) : (
          <Typography>
            Automatic groups update with school approval. Custom segments
            contain users or outreach contacts.
          </Typography>
        )}
        {!segment?.automatic && (
          <Paper sx={{ p: 2 }}>
            <Stack spacing={2}>
              <TextField
                label="Segment name"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
              <TextField
                label="Description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
              {!segment && (
                <TextField
                  select
                  label="Members"
                  value={kind}
                  onChange={(event) => setKind(event.target.value)}
                >
                  <MenuItem value="user">Users</MenuItem>
                  <MenuItem value="contact">Outreach contacts</MenuItem>
                </TextField>
              )}
              <Button
                disabled={busy || !name.trim()}
                onClick={async () => {
                  const saved = await mutate("saveSegment", {
                    id: segment?.id,
                    name,
                    description,
                    kind: segment?.kind || kind,
                  });
                  if (saved)
                    navigate(`/superuser/marketing/segments/${saved.id}`);
                }}
              >
                {segment ? "Save segment" : "Create segment"}
              </Button>
              {segment && (
                <Button
                  color="error"
                  disabled={busy}
                  onClick={async () => {
                    if (
                      window.confirm(
                        "Archive this segment? Enrollment history will remain.",
                      )
                    ) {
                      const saved = await mutate("archiveSegment", {
                        id: segment.id,
                      });
                      if (saved) navigate("/superuser/marketing/segments");
                    }
                  }}
                >
                  Archive segment
                </Button>
              )}
            </Stack>
          </Paper>
        )}
        {segmentId ? (
          segment && (
            <>
              <Accordion
                defaultExpanded
                TransitionProps={{ unmountOnExit: true }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Current members</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <SegmentResults
                    api={api}
                    segment={segment}
                    revision={revision}
                    onChanged={refreshMembers}
                  />
                </AccordionDetails>
              </Accordion>
              {!segment.automatic && (
                <Accordion TransitionProps={{ unmountOnExit: true }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Add members</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <SegmentResults
                      api={api}
                      segment={segment}
                      candidates
                      revision={revision}
                      onChanged={refreshMembers}
                    />
                  </AccordionDetails>
                </Accordion>
              )}
            </>
          )
        ) : (
          <SegmentResults api={api} revision={revision} />
        )}
        {!!operations.length && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Recent enrollment operations</Typography>
            <Button onClick={load}>Refresh</Button>
            {operations.map((row) => (
              <Stack key={row.id}>
                <Typography>
                  #{row.id}: {row.status} —{" "}
                  {Object.entries(row.counts)
                    .map(([key, count]) => `${key}: ${count}`)
                    .join("; ")}
                </Typography>
                <Button onClick={() => setOperation(row.id)}>
                  View outcomes
                </Button>
              </Stack>
            ))}
            <Stack direction="row" spacing={2}>
              <Button
                disabled={operationPage === 1}
                onClick={() => setOperationPage(operationPage - 1)}
              >
                Previous operations
              </Button>
              <Button
                disabled={operationPage * 25 >= operationTotal}
                onClick={() => setOperationPage(operationPage + 1)}
              >
                Next operations
              </Button>
            </Stack>
          </Paper>
        )}
        {operation && (
          <OperationResults
            api={api}
            id={operation}
            onClose={() => setOperation(null)}
          />
        )}
        {enrolling && (
          <SegmentEnrollment
            api={api}
            segments={[segment]}
            onClose={() => {
              setEnrolling(false);
              load();
            }}
          />
        )}
      </Stack>
    </FruitifyContainer>
  );
}

function OperationResults({ api, id, onClose }) {
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ items: [], total: 0 });
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    read(api, "operation_items", { id, page })
      .then((result) => {
        if (active) setResult(result);
      })
      .catch((error) => {
        if (active) setError(error.message);
      });
    return () => {
      active = false;
    };
  }, [api, id, page]);
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Enrollment operation #{id}</DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          {error && <Alert severity="error">{error}</Alert>}
          {result.items.map((row) => (
            <Typography key={row.id}>
              Participant #{row.recipientId}: {row.outcome}
            </Typography>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={page === 1} onClick={() => setPage(page - 1)}>
          Previous
        </Button>
        <Button
          disabled={page * 25 >= result.total}
          onClick={() => setPage(page + 1)}
        >
          Next
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function SegmentResults({
  api,
  segment,
  candidates = false,
  revision,
  onChanged,
}) {
  const [result, setResult] = useState({ items: [], total: 0 });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const segmentId = segment?.id;
  useEffect(() => {
    setSelected([]);
  }, [revision, search, page]);
  useEffect(() => {
    let active = true;
    read(
      api,
      segmentId ? (candidates ? "candidates" : "members") : "segments",
      { id: segmentId, search, page },
    )
      .then((data) => {
        if (active) {
          setResult(data);
          setError("");
        }
      })
      .catch((error) => {
        if (active) setError(error.message);
      });
    return () => {
      active = false;
    };
  }, [api, segmentId, candidates, search, page, revision]);
  const mutate = async (action, data) => {
    setBusy(true);
    try {
      await act(api, action, data);
      enqueueSnackbar(candidates ? "Members added." : "Members removed.", {
        variant: "success",
      });
      setSelected([]);
      await onChanged();
    } catch (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      <TextField
        label={
          segment
            ? candidates
              ? "Search people to add"
              : "Search current members"
            : "Search segments"
        }
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          setPage(1);
        }}
      />
      {segment && !segment.automatic && (
        <Stack direction="row" spacing={1}>
          <Button
            onClick={() => setSelected(result.items.map((row) => row.id))}
          >
            Select this page
          </Button>
          <Button
            disabled={busy || !selected.length}
            onClick={() =>
              mutate("members", {
                id: segment.id,
                ids: selected,
                remove: !candidates,
              })
            }
          >
            {candidates ? "Add selected" : "Remove selected"}
          </Button>
        </Stack>
      )}
      <List sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {result.items.map((row) => (
          <ListItem
            key={row.id}
            alignItems="flex-start"
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              bgcolor: "background.paper",
              p: { xs: 1.5, sm: 2 },
              gap: 1,
            }}
          >
            <ListItemAvatar
              sx={{ display: { xs: "none", sm: "block" }, minWidth: 44 }}
            >
              <Avatar sx={{ bgcolor: "primary.main", width: 36, height: 36 }}>
                {(row.name || row.label || "?").trim().charAt(0).toUpperCase()}
              </Avatar>
            </ListItemAvatar>
            {segment && !segment.automatic && (
              <Checkbox
                checked={selected.includes(row.id)}
                onChange={(_, checked) =>
                  setSelected((items) =>
                    checked
                      ? [...items, row.id]
                      : items.filter((id) => id !== row.id),
                  )
                }
                inputProps={{ "aria-label": `Select ${row.label}` }}
              />
            )}
            <ListItemText
              sx={{ minWidth: 0, m: 0, overflowWrap: "anywhere" }}
              primaryTypographyProps={{ fontWeight: 600 }}
              secondaryTypographyProps={{ component: "div" }}
              primary={
                segment ? (
                  row.name || row.label
                ) : (
                  <Link to={`/superuser/marketing/segments/${row.id}`}>
                    {row.name}
                  </Link>
                )
              }
              secondary={
                segment ? (
                  <Stack spacing={0.75} mt={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      {row.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {[row.role, row.schoolName || "No associated school"]
                        .filter(Boolean)
                        .join(" · ")}
                    </Typography>
                    {(row.verification || row.subjects?.length > 0) && (
                      <Box display="flex" flexWrap="wrap" gap={0.75}>
                        {row.verification && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={row.verification}
                          />
                        )}
                        {(row.subjects || []).map((subject) => (
                          <Chip key={subject} size="small" label={subject} />
                        ))}
                      </Box>
                    )}
                  </Stack>
                ) : (
                  <Stack spacing={1} mt={0.75}>
                    {row.description && (
                      <Typography variant="body2">{row.description}</Typography>
                    )}
                    <Box display="flex" flexWrap="wrap" gap={0.75}>
                      <Chip size="small" label={`${row.memberCount} members`} />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={row.automatic ? "Automatic" : "Custom"}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          row.kind === "contact" ? "Outreach contacts" : "Users"
                        }
                      />
                    </Box>
                  </Stack>
                )
              }
            />
          </ListItem>
        ))}
      </List>
      <Stack direction="row" spacing={2}>
        <Button disabled={page === 1} onClick={() => setPage(page - 1)}>
          Previous
        </Button>
        <Typography>
          Page {page} · {result.total} results
        </Typography>
        <Button
          disabled={page * 25 >= result.total}
          onClick={() => setPage(page + 1)}
        >
          Next
        </Button>
      </Stack>
    </Stack>
  );
}
